import Pusher from 'pusher-js';
import { actionTypes as profileSidebarActionTypes } from '~/profile-sidebar/reducer';
import { actionTypes as organizationsActionTypes } from '~/organizations/reducer';
import { actionTypes as queueActionTypes } from '~/queue/reducer';
import { actionTypes as draftActionTypes } from '~/drafts/reducer';
import { actionTypes as storiesActionTypes } from '~/stories/reducer';
import { actionTypes as campaignActionTypes } from '~/campaign/reducer';
import AppDispatcher from '~/composer/composer/dispatcher';
import { ActionTypes } from '~/composer/composer/state/ActionTypes';
import { actionTypes as dataFetchActionTypes } from '@bufferapp/async-data-fetch';
import {
  postParser,
  storyGroupParser,
} from '~/server/parsers';
import { IdeaEvents } from '~/content/types';
import { actionTypes as pusherActionTypes } from './reducer';

const PUSHER_APP_KEY = '646fd82bc5706097c621';

const updateEventActionMap = {
  sent_update: queueActionTypes.POST_SENT,
  updated_update: queueActionTypes.POST_UPDATED,
};

const bindProfileUpdateEvents = (channel, profileId, dispatch) => {
  // Bind other events
  channel.bind('reordered_updates', (order) => {
    dispatch({
      type: queueActionTypes.REORDERED_UPDATES,
      profileId,
      order,
    });
  });
  channel.bind('queue_paused', (paused) => {
    dispatch({
      type: profileSidebarActionTypes.PUSHER_PROFILE_PAUSED_STATE,
      paused,
      profileId,
    });
  });
  channel.bind('deleted_update', (data) => {
    dispatch({
      type: queueActionTypes.PROFILE_CAN_QUEUE_THREADS_CHANGED,
      profileId: data.profile_id,
      canQueueMoreThreads: data.can_queue_more_threads,
    });
  });
  channel.bind('added_update', (data) => {
    dispatch({
      type: queueActionTypes.PROFILE_CAN_QUEUE_THREADS_CHANGED,
      profileId: data.profile_id,
      canQueueMoreThreads: data.can_queue_more_threads,
    });
  });
  channel.bind('sent_update', (data) => {
    dispatch({
      type: queueActionTypes.PROFILE_CAN_QUEUE_THREADS_CHANGED,
      profileId: data.profile_id,
      canQueueMoreThreads: data.can_queue_more_threads,
    });
  });
  channel.bind('updated_update', (data) => {
    dispatch({
      type: queueActionTypes.PROFILE_CAN_QUEUE_THREADS_CHANGED,
      profileId: data.profile_id,
      canQueueMoreThreads: data.can_queue_more_threads,
    });
  });
  channel.bind('collaboration_draft_moved', (data) => {
    dispatch({
      type: queueActionTypes.PROFILE_CAN_QUEUE_THREADS_CHANGED,
      profileId: data.profile_id,
      canQueueMoreThreads: data.can_queue_more_threads,
    });
  });
  channel.bind('collaboration_draft_approved', (data) => {
    dispatch({
      type: queueActionTypes.PROFILE_CAN_QUEUE_THREADS_CHANGED,
      profileId: data.profile_id,
      canQueueMoreThreads: data.can_queue_more_threads,
    });
  });
};

const bindProfileStoryGroupEvents = (channel, profileId, dispatch) => {
  channel.bind('sent_story_group', (data) => {
    dispatch({
      type: storiesActionTypes.STORY_SENT,
      profileId,
      storyGroup: storyGroupParser(data.story_group),
    });
  });
  channel.bind('story_group_created', (data) => {
    dispatch({
      type: storiesActionTypes.STORY_CREATED,
      profileId,
      storyGroup: storyGroupParser(data.story_group),
    });
  });
  channel.bind('story_group_updated', (data) => {
    dispatch({
      type: storiesActionTypes.STORY_UPDATED,
      profileId,
      storyGroup: storyGroupParser(data.story_group),
    });
  });
  channel.bind('story_group_deleted', (data) => {
    dispatch({
      type: storiesActionTypes.STORY_DELETED,
      profileId,
      storyGroupId: data.story_group_id,
    });
  });
};

const channelsByProfileId = {};
const setupProfilePusherEvents = (
  { profileId, profile: { service } },
  pusher,
  dispatch
) => {
  if (profileId) {
    // If the profile is not subscribed to any channels, subscribes to private-updates channel:
    const newProfileChannels = channelsByProfileId[profileId] || {
      updates: pusher.subscribe(`private-updates-${profileId}`),
    };
    // If instagram profile and profile is not subscribed to story-groups channel, subscribes to private-story-groups channel:
    if (
      service === 'instagram' &&
      newProfileChannels.storyGroups === undefined
    ) {
      newProfileChannels.storyGroups = pusher.subscribe(
        `private-story-groups-${profileId}`
      );
    }
    channelsByProfileId[profileId] = newProfileChannels;

    bindProfileUpdateEvents(
      channelsByProfileId[profileId].updates,
      profileId,
      dispatch
    );
    if (channelsByProfileId[profileId].storyGroups) {
      bindProfileStoryGroupEvents(
        channelsByProfileId[profileId].storyGroups,
        profileId,
        dispatch
      );
    }
  }
};

const bindOrganizationEvents = (orgChannel, dispatch) => {
  // Campaigns
  orgChannel.bind('create_campaign', (data) => {
    dispatch({
      type: campaignActionTypes.PUSHER_CAMPAIGN_CREATED,
      campaign: data,
    });
  });
  orgChannel.bind('update_campaign', (data) => {
    dispatch({
      type: campaignActionTypes.PUSHER_CAMPAIGN_UPDATED,
      campaign: data,
    });
  });
  orgChannel.bind('delete_campaign', (data) => {
    dispatch({
      type: campaignActionTypes.PUSHER_CAMPAIGN_DELETED,
      campaignId: data.id,
    });
  });
  // Posts and Drafts
  orgChannel.bind('added_update', (data) => {
    if (data.update.draft) {
      dispatch({
        type: draftActionTypes.DRAFT_CREATED,
        profileId: data.profile_id,
        draft: postParser(data.update),
      });
    } else {
      dispatch({
        type: queueActionTypes.POST_CREATED,
        profileId: data.profile_id,
        post: postParser(data.update),
      });
    }
  });
  orgChannel.bind('deleted_update', (data) => {
    if (data.update.draft) {
      dispatch({
        type: draftActionTypes.DRAFT_DELETED,
        profileId: data.profile_id,
        draft: postParser(data.update),
      });
    } else {
      dispatch({
        type: queueActionTypes.POST_DELETED,
        profileId: data.profile_id,
        post: postParser(data.update),
      });
    }
  });
  // Bind moved drafts event
  orgChannel.bind('collaboration_draft_moved', (data) => {
    dispatch({
      type: draftActionTypes.DRAFT_MOVED,
      profileId: data.profile_id,
      draft: postParser(data.draft),
    });
  });
  orgChannel.bind('collaboration_draft_approved', (data) => {
    dispatch({
      type: draftActionTypes.DRAFT_APPROVED,
      profileId: data.profile_id,
      draft: postParser(data.draft),
    });
  });
  orgChannel.bind('collaboration_draft_updated', (data) => {
    dispatch({
      type: draftActionTypes.DRAFT_UPDATED,
      profileId: data.profile_id,
      draft: postParser(data.draft),
    });
  });
  orgChannel.bind('added_post_comment', (data) => {
    dispatch({
      type: draftActionTypes.DRAFT_COMMENT_ADDED,
      profileId: data.profile_id,
      postId: data.update_id,
      comments: data.update.comments,
    });
  });
  orgChannel.bind('deleted_post_comment', (data) => {
    dispatch({
      type: draftActionTypes.DRAFT_COMMENT_DELETED,
      profileId: data.profile_id,
      postId: data.update_id,
      comments: data.update.comments,
    });
  });
  Object.entries(updateEventActionMap).forEach(([pusherEvent, actionType]) => {
    orgChannel.bind(pusherEvent, (data) => {
      dispatch({
        type: actionType,
        profileId: data.profile_id,
        post: postParser(data.update),
      });
    });
  });
  Object.values(IdeaEvents).forEach((eventName) => {
    orgChannel.bind(eventName, (data) => {
      dispatch({
        eventName,
        type: `PUSHER_EVENT/${eventName}`,
        data,
      });
    });
  });
};

const bindUserEvents = (userChannel, dispatch) => {
  // Profile Groups
  userChannel.bind('created_profile_group', (data) => {
    const { id, name, profile_ids: profileIds } = data;
    AppDispatcher.handlePusherAction({
      actionType: ActionTypes.COMPOSER_PROFILE_GROUP_CREATED,
      groupData: { id, name, profileIds },
    });
  });
  userChannel.bind('edited_profile_group', (data) => {
    const { id, name, profile_ids: profileIds } = data;
    AppDispatcher.handlePusherAction({
      actionType: ActionTypes.COMPOSER_PROFILE_GROUP_UPDATED,
      groupData: { id, name, profileIds },
    });
  });
  userChannel.bind('deleted_profile_group', (data) => {
    const { id } = data;
    AppDispatcher.handlePusherAction({
      actionType: ActionTypes.COMPOSER_PROFILE_GROUP_DELETED,
      groupData: { id },
    });
  });

  // Video transcoded
  userChannel.bind('transcode_done', (data) => {
    // Dispatch video transcoded for all pages
    dispatch({
      type: pusherActionTypes.EVENT_VIDEO_TRANSCODED,
      args: {
        uploadId: data.upload_id,
        durationMs: data.media.video.details?.duration_millis,
        size: data.media.video.details?.file_size,
        width: data.media.video.details?.width,
        height: data.media.video.details?.height,
        url: data.media.video.details?.transcoded_location,
        thumbnail: data.media.thumbnail,
      },
    });

    // Dispatch video transcoded for the composer
    AppDispatcher.handlePusherAction({
      actionType: ActionTypes.COMPOSER_VIDEO_PROCESSED,
      processedVideoData: {
        uploadId: data.upload_id,
        name: data.media.video.title,
        duration: data.media.video.details?.duration,
        durationMs: data.media.video.details?.duration_millis,
        size: data.media.video.details?.file_size,
        width: data.media.video.details?.width,
        height: data.media.video.details?.height,
        url: data.media.video.details?.transcoded_location,
        originalUrl: data.media.video.details?.location,
        thumbnail: data.media.thumbnail,
        availableThumbnails: data.media.video.thumbnails,
      },
    });
  });
};

/**
 * Middleware
 */
export default ({ dispatch }) => {
  const pusher = new Pusher(PUSHER_APP_KEY, { authEndpoint: '/pusher/auth' });
  window.__pusher = pusher;

  const subscribeOrganizationEvents = (organizationId) => {
    if (organizationId) {
      const channelName = `private-updates-org-${organizationId}`;
      const orgChannel = pusher.subscribe(channelName);
      bindOrganizationEvents(orgChannel, dispatch);
    }
  };

  const subscribeUserEvents = (userId) => {
    if (userId) {
      const channelName = `private-updates-${userId}`;
      const userChannel = pusher.subscribe(channelName);
      bindUserEvents(userChannel, dispatch);
    }
  };

  return (next) => (action) => {
    next(action);

    switch (action.type) {
      case profileSidebarActionTypes.SELECT_PROFILE:
        setupProfilePusherEvents(action, pusher, dispatch);
        break;
      case organizationsActionTypes.ORGANIZATION_SELECTED: {
        const { id: organizationId } = action.selected;
        subscribeOrganizationEvents(organizationId);
        break;
      }
      case `user_${dataFetchActionTypes.FETCH_SUCCESS}`: {
        const { id: userId } = action.result;
        subscribeUserEvents(userId);
        break;
      }
      default:
        break;
    }
  };
};
